import React, { useState, useEffect } from "react";
import axios from "axios";
import ErrorPopup from "../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../utils/popup/SuccessPopup";
import GetCookie from "../../utils/GetCookie";
import PaginationControls from "../../utils/PaginationControls";

const HistoryList = ({ onLoadHistory }) => {
  // History list state
  const [historyList, setHistoryList] = useState([]);
  
  // Pagination and search states
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [total, setTotal] = useState(0);
  
  // Delete history states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [historyToDelete, setHistoryToDelete] = useState(null);
  
  // Loading and error states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState("");
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState("");
  
  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };
  
  // Fetch history list on component mount and when pagination changes
  useEffect(() => {
    fetchHistoryList();
  }, [page, size]);
  
  // Fetch history list from API
  const fetchHistoryList = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage("Fetching chat history");
    
    try {
      const response = await axios.post(
        '/pceapi/copilot/list-history',
        { 
          page: page,
          size: size
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        window.location.href = '/login';
        return;
      }
      
      if (data.status === 'failed') {
        const errorMessage = data.message || 'Failed to load chat history';
        setIsErrorPopupVisible(true);
        setPopupErrorMessage(errorMessage);
        return;
      }
      
      // Set the history data
      setHistoryList(data.data || []);
      setTotal(data.total || 0);
      
    } catch (error) {
      console.error("Error fetching history list:", error);
      const errorMessage = error.message || 'Failed to load chat history';
      setIsErrorPopupVisible(true);
      setPopupErrorMessage(errorMessage);
    } finally {
      setIsProcessingPopupVisible(false);
      setProcessingMessage("");
    }
  };
  
  // Handle loading a specific history session
  const handleLoadHistory = async (sessionId) => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage("Loading conversation");
    
    try {
        const response = await axios.post(
            '/pceapi/copilot/load-history',
            { session_id: sessionId },
            { headers }
        );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        window.location.href = '/login';
        return;
      }
      
      if (data.status === 'failed') {
        const errorMessage = data.message || 'Failed to load conversation';
        setIsErrorPopupVisible(true);
        setPopupErrorMessage(errorMessage);
        return;
      }
      
      // Call parent function to handle the loaded history
      if (onLoadHistory) {
        onLoadHistory(data.data, sessionId);
      }
      
    } catch (error) {
      console.error("Error loading history:", error);
      const errorMessage = error.message || 'Failed to load conversation';
      setIsErrorPopupVisible(true);
      setPopupErrorMessage(errorMessage);
    } finally {
      setIsProcessingPopupVisible(false);
      setProcessingMessage("");
    }
  };
  
  // Show delete confirmation dialog
  const confirmDelete = (session) => {
    setHistoryToDelete(session);
    setShowDeleteConfirm(true);
  };
  
  // Handle deleting a history session
  const handleDeleteHistory = async () => {
    setShowDeleteConfirm(false);
    
    if (!historyToDelete) return;

    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting conversation: ${historyToDelete.session_title || 'Untitled'}`);
    
    try {
      const response = await axios.post(
        '/pceapi/copilot/delete-history',
        { session_id: historyToDelete.session_id },
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        window.location.href = '/login';
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        const errorMessage = data.message || 'Failed to delete conversation';
        setIsErrorPopupVisible(true);
        setPopupErrorMessage(errorMessage);
        return;
      }
      
      // Show success message
      setPopupSuccessMessage(data.message || 'Chat history deleted successfully');
      setIsSuccessPopupVisible(true);
      
    } catch (error) {
      console.error("Error deleting history:", error);
      const errorMessage = error.message || 'Failed to delete conversation';
      setIsErrorPopupVisible(true);
      setPopupErrorMessage(errorMessage);
    } finally {
      setIsProcessingPopupVisible(false);
      setProcessingMessage("");
      setHistoryToDelete(null);
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };
  
  // Close popups
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    // Refresh the history list after successful deletion
    fetchHistoryList();
  };
  
  return (
    <div>
      <div className="sidebar-inner ev-card2" style={{overflowY:"hidden"}}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <p className="ev-reg-p-dark mb-0">
            Chat History <span style={{fontWeight:"500"}}>({total})</span>
          </p>
        </div>
        
          <div style={{textAlign:"right"}}> 
            <button 
              className="ev-btn-dark-fill" 
              style={{fontSize:"var(--fs-xs)"}} 
              onClick={fetchHistoryList}
            >
              Refresh <i className="bi bi-arrow-repeat"></i>
            </button>
          </div>

        
        <div className="tree-container mt-2 scrollable-element" style={{position:"relative", marginRight:"-15px"}}>
          {historyList.length === 0 ? (
            <div className="text-center mt-5">
              <p className="ev-small-p-blue">No chat history found</p>
            </div>
          ) : (
            <div style={{position:"absolute", width:"95%"}}>
              {historyList.map(session => (
                <div key={session.session_id} className="ev-card1 mb-2">
                  
                  {/* Session Title */}
                  <div style={{textAlign:"right"}}>
                    <span class="message-card3 avatar-tooltip" style={{fontSize:"var(--fs-xxs)"}} data-tooltip={session.fabric_name}>
                    <b><i className="bi bi-boxes"></i> Project. </b>
                      {session.fabric_name.length > 25
                        ? session.fabric_name.substring(0, 25) + '...' 
                      : session.fabric_name} 
                    </span>
                  </div>
                  <div className="mb-2">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-chat-left-text me-2 ev-reg-p-blue"></i>
                      <span className="ev-small-p-dark" style={{ fontWeight: "600" }}>
                        {session.session_title && session.session_title.length > 30
                          ? session.session_title.substring(0, 30) + '...' 
                          : session.session_title || 'Untitled Conversation'}
                      </span>
                    </div>
                  </div>
                  
                  
                  {/* Action Buttons */}
                  <div className="d-flex justify-content-between">
                    <div className="d-flex align-items-center w-50">
                      <span className="ev-xsmall-p-dark">
                        <i className="bi bi-clock me-2 ev-small-p-blue"></i> {formatDate(session.updated_at)}
                      </span>
                    </div>
                    <button 
                      className="ev-btn-lightblue-fill ms-1" 
                      onClick={() => handleLoadHistory(session.session_id)}
                      style={{ fontSize: "10px", padding: "4px 12px" }}
                    >
                     <i className="bi bi-chat-left-text"></i>&nbsp; Open Chat
                    </button>
                    <button 
                        className="btn btn-sm btn-danger ms-1 avatar-tooltip"
                        style={{ fontSize: "10px", padding: "4px 8px" }}
                        onClick={() => confirmDelete(session)}
                        data-tooltip="Delete History"
                    >
                    <i className="bi bi-trash"></i>
                    </button>
                  </div>
                  
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Pagination for sidebar */}
        <div className="d-flex justify-content-between align-items-center mt-3 sidebar-inner-bottom-fix-ele" style={{fontSize:"var(--fs-sm)"}}>
          <div>
            <button 
              className="ev-btn-light-fill" 
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              style={{padding:"2px 5px", fontSize:"var(--fs-sm)"}}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            
            <span className="ev-small-p-dark ms-2">
              Page {page} of {Math.ceil(total / size) || 1}
            </span>
            
            <button 
              className="ev-btn-light-fill ms-2" 
              disabled={page >= Math.ceil(total / size)}
              onClick={() => setPage(page + 1)}
              style={{padding:"2px 5px", fontSize:"var(--fs-sm)"}}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
          <div className="justify-content-between align-items-center col-3 mt-1" style={{fontSize:"var(--fs-sm)", textAlign:"right"}}>
            <PaginationControls 
              initialSize={size}
              onSizeChange={setSize}
            />
          </div>
        </div>
      </div>

      {/* Error, Success, and Processing popups */}
      {isErrorPopupVisible && (
        <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />
      )}

      {isSuccessPopupVisible && (
        <SuccessPopup onClose={closeSuccessPopup} message={popupSuccessMessage} />
      )}
      
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && historyToDelete && (
        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content poppinsfont">
              <div className="modal-header">
               <p className="mb-0" style={{fontSize:"var(--fs-xl)",fontWeight:"600"}}>Confirm Delete Conversation</p>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteConfirm(false)}
                ></button>
              </div>
              <div className="modal-body" style={{fontSize:"var(--fs-md)"}}>
                <p>Are you sure you want to delete this conversation?</p>
                <div className="mb-2">
                  <strong>Conversation:</strong> {historyToDelete.session_title || 'Untitled Conversation'}
                </div>
                <div className="mb-2">
                  <strong>Project:</strong> {historyToDelete.fabric_name}
                </div>
                <div className="mb-3">
                  <strong>Last Updated:</strong> {formatDate(historyToDelete.updated_at)}
                </div>
                <p className="text-danger mb-0">This will permanently delete all messages and data from this conversation. This action cannot be undone.</p>
              </div>
              <div className="modal-footer" >
                <button 
                  type="button" 
                  className="btn btn-secondary" style={{fontSize:"var(--fs-md)"}}
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" style={{fontSize:"var(--fs-md)"}}
                  onClick={handleDeleteHistory}
                >
                  Delete Conversation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryList;